import { fetchMeetConditionsApi } from '@/api/public';
import { ListProduct, WishProduct } from '@/api/types/public';
import { fetchAddWish, fetchCancelWish } from '@/api/wish';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { useSpmDomAttr, withSpmCnt } from '@/reporting/spm';

import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { getRouteCnName } from '@/utils/route-metadata';
import { randomFuncName } from '@/utils/tools';
import { Button } from '@mui/material';
import { WishFilled, WishOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { usePathname, useRouter } from 'next/navigation';
import { memo, useContext, useEffect, useMemo, useState } from 'react';
import { ResponsiveContext } from '../Responsive/ResponsiveProvider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';

const Wish = ({
  data,
  className,
  iconClassName = '',
  wishNumClassName = '',
  iconSize = '22px',
  showWishNum = false,
  loginSituation = '',
  removed = () => {},
  wished = () => {},
  onCollectClick = () => {},
  collectScene = '其它',
  price = '', // 添加price prop用于App收藏
  spmCnt, // 收藏坑位 SPM 编码（`${楼层动态模块编码}.favorite_NNN`）；由调用方拼接后传入，缺省不附加
}: {
  data: (ListProduct | WishProduct) & {
    business_sku?: string;
    sku?: string;
  };
  className: string;
  iconClassName?: string;
  wishNumClassName?: string;
  iconSize?: string;
  showWishNum?: boolean;
  loginSituation?: string;
  removed?: () => void;
  wished?: () => void;
  onCollectClick?: () => void;
  collectScene?: string;
  price?: string; // 添加price类型定义
  /** 收藏坑位 SPM 编码（`${spmModule}.favorite_NNN`，D 位）；调用方未拼接时为 undefined，事件保持原 properties。 */
  spmCnt?: string;
}) => {
  const [wishNumStr, setWishNumStr] = useState('');
  const [wishNum, setWishNum] = useState(data.wish_num);
  const getGlobalWishNum = useStore((state) => state.getWishNum);
  const [isWish, setIsWish] = useState(data.is_wish);
  const token = useStore((state) => state.token);
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  const refreshWishNum = useStore((state) => state.setWishNum);
  const { deviceType } = useContext(ResponsiveContext);
  const [loading, setLoading] = useState(false);
  const { trackEvent } = useReporting();
  const t = useTranslations('product');
  const router = useRouter();
  const pathname = usePathname();
  const waiMode = useStore((state) => state.waiMode);
  // Data SPM DOM 打标（请求头 data-spm=1 开关）：与收藏上报 spm_cnt 同值（favorite_NNN D 位）；
  // 调用方未传编码或开关关闭时为 undefined，零 DOM 差异。
  const wishDomSpm = useSpmDomAttr(spmCnt);

  // App相关状态
  const syncAppLoginToken = useStore((state) => state.syncAppLoginToken);

  const wishNumWithK = (_num: number) => {
    const num = Number(_num);
    if (Number.isNaN(num)) return '0';
    if (num < 0) return '0';
    if (num < 1000) {
      return `${num}`;
    }
    if (num === 1000) {
      return '1K';
    }
    if (num > 1000) {
      let newStr = '';
      let count = 0;
      const numK = (num / 1000).toFixed(1);
      const strArr = String(numK).split('.');
      let integerStr = strArr[0];
      for (let i = integerStr.length - 1; i >= 0; i--) {
        if (count % 3 === 0 && count !== 0) {
          newStr = `${integerStr.charAt(i)},${newStr}`;
        } else {
          newStr = integerStr.charAt(i) + newStr;
        }
        count++;
      }

      integerStr = `${newStr}${strArr[1] === '0' ? '' : `.${strArr[1]}`}K`;
      return integerStr;
    }
  };

  const userInfo = useStore((state) => state.userInfo);

  const getMeetConditions = async () => {
    if (deviceType === EPlatform.WAP) {
      const res = await fetchMeetConditionsApi({ condition_2: 1 });
      if (res.code === 200) {
        if (res.data.is_meet_conditions) {
          router.push(`/activity-page?id=${res.data.active_page_id}`);
        }
      }
    }
  };

  // 优化：使用更精确的依赖项，避免对象引用问题
  const toggle = async () => {
    const collectProperties = {
      page_name: getRouteCnName(pathname),
      url_path: window.location.href,
      virtually_no: userInfo?.uuid ? `${userInfo.uuid}${Date.now()}` : `null${Date.now()}`,
      shop_sku: data?.shop_sku,
      business_sku: data?.business_sku,
      product_sku: data?.sku,
      product_id: `${data?.id}`,
      product_name: data?.short_name,
      collect_scene: collectScene || t('wish.collectScene'),
    };
    trackEvent(
      {
        eventName: 'Collect_Click',
        // 收藏坑位（favorite_NNN）D 位编码；无作用域时保持原 properties 不附加。
        properties: spmCnt ? withSpmCnt(collectProperties, spmCnt) : collectProperties,
      },
      [ProviderType.SENSORS],
    );

    if (isWish !== 1) {
      // TODO: 临时放这里
      trackEvent(
        {
          eventName: 'AddToWishlist',
          properties: {
            content_type: 'product',
            content_ids: data?.shop_sku,
          },
        },
        [ProviderType.FACEBOOK],
      );
      trackEvent(
        {
          eventName: 'wishlist',
          properties: {
            offerId: data?.shop_sku,
          },
        },
        [ProviderType.RTB_HOUSE],
      );
      trackEvent(
        {
          eventName: 'wish_product',
          properties: {
            event_label: 'Ck_wish',
            event_category: 'PRODUCT_LIST',
            sku: data?.shop_sku,
          },
        },
        [ProviderType.GA4],
      );
    }
    setLoading(true);
    if (!token) {
      sessionStorage.setItem('zl-visitor-wish-product-id', data.id.toString());
      if ([EPlatform.ANDROID, EPlatform.IOS].includes(deviceType)) {
        // 在App内嵌环境下，使用Vue组件相同的逻辑处理登录
        try {
          // 生成随机回调函数名
          const logincallbackFnName = randomFuncName('JumpToPage');

          // 注册回调函数到window对象
          (window as any)[logincallbackFnName] = async (val: any) => {
            await syncAppLoginToken(val);
          };

          // 发送登录请求给App，包含Callback参数
          const loginData = {
            RouteName: '/login', // 页面路由名称
            closedCurrentPage: false, // 跳转后是否关闭当前页面
            backToken: true, // 是否需要返回token信息
            Callback: logincallbackFnName, // 回调函数名
          };

          // 使用appCommunicationManager发送消息
          appCommunicationManager.sendMessage(EAppCommunicationFuncName.JUMP_TO_PAGE, loginData);
        } catch (error) {
          logger.error('App登录跳转失败:', error);
        }
        setLoading(false);
        return;
      }
      setShowLoginRegisterDialog({
        show: true,
        eventType: 'collect',
        situation: loginSituation || '收藏',
      });
      setLoading(false);
      return;
    }
    if ([EPlatform.ANDROID, EPlatform.IOS].includes(deviceType)) {
      try {
        const callbackFnName = randomFuncName('ProductWishMethod');
        (window as any)[callbackFnName] = (val: any) => {
          if (val) {
            logger.log('App收藏回调数据:', val);
            // 更新收藏状态
            // 这里需要根据App返回的数据更新本地状态
            // 由于React组件没有$emit，我们需要通过其他方式通知父组件
            // 可以通过调用removed()或wished()来通知父组件
            if (isWish === 2) {
              setIsWish(1);
              setWishNum((prev) => prev + 1);
              wished();
              if (![EPlatform.ANDROID, EPlatform.IOS].includes(deviceType)) {
                refreshWishNum(getGlobalWishNum() + 1);
              }
            } else {
              setIsWish(2);
              setWishNum((prev) => prev - 1);
              removed();
              if (![EPlatform.ANDROID, EPlatform.IOS].includes(deviceType)) {
                refreshWishNum(getGlobalWishNum() - 1);
              }
            }
          }
        };

        const wishData = {
          id: data.id, // 商品ID
          shopSku: data.shop_sku, // 销售SKU
          businessSku: data.business_sku, // 平台sku
          sku: data.sku, // 商品SKU
          shortName: data.short_name, // 商品简称
          finalPrice: price, // 使用传入的price prop
          isWish: isWish === 1, // 当前收藏的状态
          wishNum: data.wish_num, // 当前收藏的数量
          Callback: callbackFnName,
        };

        logger.log('App收藏数据:', wishData);

        // 使用appCommunicationManager发送消息
        appCommunicationManager.sendMessage(
          EAppCommunicationFuncName.PRODUCT_WISH_METHOD,
          wishData,
        );
      } catch (error) {
        logger.error('App收藏方法调用失败:', error);
      }
      setLoading(false);
      return;
    }

    const collectBtnProperties = {
      virtually_no: userInfo?.uuid ? `${userInfo.uuid}${Date.now()}` : `null${Date.now()}`,
      shop_sku: data?.shop_sku,
      business_sku: data?.business_sku,
      product_sku: data?.sku,
      product_id: `${data?.id}`,
      product_name: data?.short_name,
      page_name: getRouteCnName(pathname),
      url_path: window.location.href,
      collect_type: isWish === 1 ? '取消收藏' : '收藏',
    };
    trackEvent(
      {
        eventName: 'CollectBtn_Click',
        // 同一收藏坑位的补充事件，附加相同 D 位编码；无作用域时保持原 properties。
        properties: spmCnt ? withSpmCnt(collectBtnProperties, spmCnt) : collectBtnProperties,
      },
      [ProviderType.SENSORS],
    );

    try {
      if (isWish === 1) {
        const res = await fetchCancelWish({ product_id: [data.id] });
        if (res.code === 200) {
          setIsWish(2);
          // 修复：取消收藏应该减少数量
          setWishNum((prev) => prev - 1);
          removed();
          if (![EPlatform.ANDROID, EPlatform.IOS].includes(deviceType)) {
            refreshWishNum(getGlobalWishNum() - 1);
          }
          setLoading(false);
        }
      } else {
        getMeetConditions();
        const res = await fetchAddWish({
          product_id: data.id,
        });
        if (res.code === 200) {
          setIsWish(1);
          // 修复：添加收藏应该增加数量
          setWishNum((prev) => prev + 1);
          wished();
          if (![EPlatform.ANDROID, EPlatform.IOS].includes(deviceType)) {
            refreshWishNum(getGlobalWishNum() + 1);
          }
          setLoading(false);
        }
      }
    } catch (err) {
      logger.error(err);
    }
    setLoading(false);
    onCollectClick();
  };

  useEffect(() => {
    setWishNumStr(wishNumWithK(wishNum));
  }, [wishNum]);

  const handleAppWishData = (
    event: CustomEvent<
      Record<
        string,
        {
          productId: string;
          shopSku: string;
          wishNum: number;
          isWish: boolean;
        }
      >
    >,
  ) => {
    if (event.detail[data.id]) {
      setIsWish(event.detail[data.id].isWish ? 1 : 2);
      setWishNum(event.detail[data.id].wishNum);
      if (event.detail[data.id].isWish) {
        wished();
      } else {
        removed();
      }
    }
  };

  useEffect(() => {
    if (token && sessionStorage.getItem('zl-visitor-wish-product-id') === data.id.toString()) {
      // 之前遗留的bug，没有删除未登录前点击的wish的商品
      sessionStorage.removeItem('zl-visitor-wish-product-id');
      toggle();
    }
    if ([EPlatform.ANDROID, EPlatform.IOS].includes(deviceType) && token) {
      window.addEventListener('appWishData', handleAppWishData);
    }
    return () => {
      window.removeEventListener('appWishData', handleAppWishData);
    };
  }, [token]);

  useEffect(() => {
    setWishNum(data.wish_num);
    if (!token) {
      // 登出瞬间父级 data 往往仍是登录态快照，直接用 data.is_wish 会盖掉本地已取消收藏(2)，
      // 等列表接口返回后才变回 2，出现「先亮已收藏再灭」的闪烁。
      setIsWish((prev) => {
        if (data.is_wish === 2) return 2;
        if (prev === 2) return 2;
        if (prev === 1 && data.is_wish === 1) return 2;
        return data.is_wish;
      });
      return;
    }
    setIsWish(data.is_wish);
  }, [data.is_wish, data.wish_num, token]);

  const buttonAriaLabel = useMemo(() => {
    if (!waiMode) return '';
    return isWish === 1 ? 'Remove from wishlist' : 'Add to wishlist';
  }, [isWish, waiMode]);

  return (
    <span
      className={`${className} p-0 cursor-default`}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <Button
        variant="text"
        loading={loading}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          toggle();
        }}
        aria-label={buttonAriaLabel}
        title={buttonAriaLabel}
        {...wishDomSpm}
        sx={{
          padding: '0!important',
          minWidth: '0!important',
          '&:hover': { background: 'none' },
        }}
      >
        {isWish === 1 ? (
          <WishFilled
            className={`cursor-pointer text-primary-purple text-[${iconSize}] ${iconClassName} hover:text-primary-purple-hover`}
          />
        ) : (
          <div
            className="relative"
            style={{
              height: iconSize,
              width: iconSize,
            }}
          >
            <WishOutlined
              className={`absolute top-0 left-0 z-2 cursor-pointer text-primary-text text-[${iconSize}] ${iconClassName} hover:text-primary-purple-hover`}
            />
            <WishFilled
              className={`absolute top-0 left-0 z-1 cursor-pointer text-primary-white fill-primary-white opacity-[0.6] text-[${iconSize}] ${iconClassName}`}
            />
          </div>
        )}
      </Button>
      {showWishNum && <span className={`${wishNumClassName}`}>{wishNumStr}</span>}
    </span>
  );
};

export default memo(Wish);
